import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';

const TEAL   = '#14b8a6';
const GREEN  = '#34d399';
const AMBER  = '#f59e0b';
const RED    = '#ef4444';
const CARD   = '#1e2329';
const SUNK   = '#171b21';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const TEXT   = '#e5e7eb';
const DIM    = '#9ca3af';
const INPUT  = '#141920';

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, fontFamily: 'system-ui', padding: 20 },
  card:    { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, width: '100%', maxWidth: 660, maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
  head:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px 15px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 },
  title:   { fontSize: 15, fontWeight: 700, color: TEXT, display: 'flex', alignItems: 'center', gap: 9 },
  body:    { padding: '20px 22px 24px', overflowY: 'auto', flex: 1 },
  footer:  { padding: '13px 22px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: `1px solid ${BORDER}`, flexShrink: 0 },

  label:   { display: 'block', marginBottom: 6, fontSize: 11, color: MUTED, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  input:   { width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: INPUT, border: `1px solid ${BORDER}`, borderRadius: 8, color: TEXT, fontSize: 14, outline: 'none' },
  btnPrim: { padding: '9px 20px', background: TEAL, color: '#04201d', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  btnGhost:{ padding: '9px 16px', background: 'transparent', color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 8, cursor: 'pointer', fontSize: 13 },

  eyebrow: { fontSize: 10, color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em' },
  row:     { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 14, padding: '5px 0', fontSize: 13, color: DIM },
  val:     { color: TEXT, fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' },
};

const nf = n => (Number(n) || 0).toLocaleString();

const SHIFT_NAMES = ['morning', 'afternoon', 'night'];
const addedShiftName = (total = 2) => SHIFT_NAMES[total - 1] || `shift ${total}`;
const shiftListName  = (total = 2) => SHIFT_NAMES.slice(0, total).join(' + ');

// What a part still needs before it can be scheduled at all. These are exactly
// the three setup blockers the engine raises, checked here so an impossible
// question can be headed off rather than answered.
function partReadiness(p) {
  if (!p) return { ready: true, missing: [] };
  const missing = [];
  if (!p.mould_id) missing.push('a mould');
  if (!p.target_cycle_s) missing.push('a cycle time');
  if (!p.primary_machine_id && !(p.secondary_machine_ids || []).length) missing.push('a machine');
  return { ready: missing.length === 0, missing };
}

function listAnd(items) {
  if (items.length <= 1) return items[0] || '';
  return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
}

function addDaysStr(d, n) {
  const x = new Date(d + 'T12:00:00');
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
}
function isWeekendStr(d) {
  const day = new Date(d + 'T12:00:00').getDay();
  return day === 0 || day === 6;
}
function daysBetweenStr(a, b) {
  return Math.round((new Date(b + 'T12:00:00') - new Date(a + 'T12:00:00')) / 86400000);
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtShort(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// "14–17 Oct, 20 Oct" — a director reading which nights they would be paying
// for wants spans, not a column of twelve dates.
function dateRanges(dates) {
  if (!dates || dates.length === 0) return '—';
  const runs = [];
  let start = dates[0], prev = dates[0];
  for (let i = 1; i < dates.length; i++) {
    const gap = (new Date(dates[i] + 'T12:00:00') - new Date(prev + 'T12:00:00')) / 86400000;
    if (gap <= 3) { prev = dates[i]; continue; }   // Fri→Mon still reads as one span
    runs.push([start, prev]);
    start = prev = dates[i];
  }
  runs.push([start, prev]);
  return runs.map(([a, b]) => (a === b ? fmtShort(a) : `${fmtShort(a)} – ${fmtShort(b)}`)).join(', ');
}

// ─────────────────────────────────────────────────────────────────────────────
// Turning the engine's answer into a decision.
//
// The question is "can we take this order?", but the useful answer is almost
// never yes or no — it is "yes, if you move one job", or "yes, for 6,000 of
// them". So the verdict states the best outcome available and the thing that
// gets you there leads. Every option carries its price, because the price is
// what the director is actually choosing between.
// ─────────────────────────────────────────────────────────────────────────────
// The knock-on effects of moving a run, put into a sentence. This is the line
// that decides whether a director trusts the recommendation, so it says what
// was checked and what was not.
function cascadeLine(c, runName) {
  if (!c) return null;
  const movedOthers = (c.moved || []).filter(m => m.plan_id !== undefined).length - 1;
  if (c.breaks && c.breaks.length > 0) {
    const worst = c.breaks[0];
    return {
      safe: false,
      text: `Moving this shifts ${c.moved.length} run${c.moved.length === 1 ? '' : 's'} on the machine, and `
          + `${worst.delivery_week ? `Wk ${worst.delivery_week} ` : ''}${worst.part} would miss its deadline by `
          + `${worst.late_by} working day${worst.late_by === 1 ? '' : 's'}.`,
    };
  }
  if (movedOthers <= 0) {
    return {
      safe: true,
      text: `${runName} moves later; nothing behind it on the machine has to shift. No other delivery is affected.`,
    };
  }
  return {
    safe: true,
    text: `${runName} moves later and ${movedOthers} run${movedOthers === 1 ? '' : 's'} behind it shift too. `
        + `Every one still makes its own delivery week — nothing is cancelled and no date moves.`,
  };
}

function restartOf(d) {
  const me = (d.cascade?.moved || []).find(m => m.plan_id === d.plan_id);
  return me ? me.new_start : null;
}

function moveStep(d) {
  const restart = restartOf(d);
  if (!restart) return { verb: 'Move', what: weekName(d), where: d.machine, when: 'later' };
  return {
    verb: 'Move',
    what: weekName(d),
    where: d.machine,
    whenPre: 'new start',
    highlight: fmtDate(restart),
    whenPost: `— was ${fmtShort(d.start)}`,
  };
}

function weekName(d) {
  return d.delivery_week ? `Wk ${d.delivery_week} ${d.part}` : d.part;
}

function decide(r, partName = '', askedWeek = '') {
  const {
    feasible, failure, reason, blockers = [], partial,
    planned_qty = 0, requested_qty = 0, meets_target, weeks_late, at_risk,
    earliest_week, finish, second_shift: ss, alternatives: alt,
  } = r;

  if (failure === 'setup') {
    return {
      tone: RED, icon: 'fa-screwdriver-wrench', kind: 'setup',
      headline: "This part isn't ready to schedule",
      sub: 'A data problem, not a capacity one — the board may well have room.',
      options: [], blockers,
    };
  }

  const options = [];

  // Proven first: each of these was checked by replanning, so it is known to work.
  for (const d of (alt?.displacement || [])) {
    // Two levers pulled together. Neither alone gets there, which is exactly why
    // this has to be offered as its own option rather than left to be inferred.
    if (d.combined_with_second_shift) {
      options.push({
        key: `combo-${d.plan_id}`,
        icon: 'fa-layer-group',
        resolves: !!d.resolves,
        title: `Move ${weekName(d)} and run a second shift`,
        costSays: d.shift_cost ? d.shift_cost.says : null,
        declare: (d.shift_machine_ids || []).length && (d.shift_dates || []).length
          ? { machineIds: d.shift_machine_ids, machines: d.machine ? [d.machine] : [], dates: d.shift_dates, cost: d.shift_cost || null,
              first: `Move ${d.part}${d.order_number ? ` · ${d.order_number}` : ''} on the board first; the window alone does not make room.` }
          : null,
        price: (() => {
          const n = d.double_shift_days ?? (d.shift_dates || []).length;
          return `${d.undated ? 'Free move' : `Free move — ${d.slack_days} days of slack`}`
               + ` + ${n} ${addedShiftName(2)} shift${n === 1 ? '' : 's'}`;
        })(),
        detail: `Push ${d.part}${d.order_number ? ` · ${d.order_number}` : ''} back from ${fmtShort(d.start)}`
              + `${d.days > 1 ? ` – ${fmtShort(d.finish)}` : ''}${d.delivery_week ? ` (due Wk ${d.delivery_week}, so there is room)` : ''}, `
              + `then run your order on ${shiftListName(2)} shifts, ${dateRanges(d.shift_dates)}.`,
        note: 'Neither on its own is enough — together they are',
        dayPlan: d.day_plan || [],
        movedDays: d.days || 0,
        ladder: (() => {
          const plain = (alt?.displacement || [])
            .find(x => x.plan_id === d.plan_id && !x.combined_with_second_shift);
          const afterMove = plain ? requested_qty - (plain.remaining_shortfall || 0) : null;
          return [
            afterMove != null ? { label: `move ${weekName(d)}`, qty: afterMove } : null,
            { label: `add ${addedShiftName(2)} shifts`, qty: requested_qty },
          ].filter(Boolean);
        })(),
        steps: [
          moveStep(d),
          { verb: 'Run', what: `your order on ${shiftListName(2)} shifts`,
            when: (() => {
              const n = d.double_shift_days ?? (d.shift_dates || []).length;
              return `${dateRanges(d.shift_dates)} · ${n} day${n === 1 ? '' : 's'} with an ${addedShiftName(2)} shift`;
            })() },
        ],
        planShifts: 2,
      facts: [
        { label: 'Due', value: d.delivery_week ? `Wk ${d.delivery_week}` : 'no week set' },
        { label: 'Order', value: d.order_number },
        { label: 'Machine', value: d.machine },
        { label: 'Runs now', value: `${fmtShort(d.start)}${d.days > 1 ? ` – ${fmtShort(d.finish)}` : ''}` },
        { label: 'Moves to', value: restartOf(d) ? fmtShort(restartOf(d)) : null, hi: true },
        { label: 'Slack', value: d.undated ? 'unlimited' : `${d.slack_days} days` },
      ],
      cascade: cascadeLine(d.cascade, weekName(d)),
      safe: d.cascade ? d.cascade.ok !== false : true,
      gantt: {
        planDates: d.plan_dates || [],
        displacedPlanId: d.plan_id,
        moved: d.cascade?.moved || [],
        planShifts: d.combined_with_second_shift ? 2 : 1,
      },
        outcome: d.resolves
          ? `All ${nf(requested_qty)} finish ${fmtDate(d.new_finish)}`
          : `Gets much closer — ${nf(d.remaining_shortfall)} still short`,
      });
      continue;
    }
    options.push({
      key: `disp-${d.plan_id}`,
      icon: 'fa-right-left',
      resolves: !!d.resolves,
      title: `Move ${weekName(d)}`,
      price: d.undated
        ? 'Free — it has no delivery week to miss'
        : `Free — it has ${d.slack_days} working day${d.slack_days === 1 ? '' : 's'} of slack`,
      detail: `Currently ${fmtShort(d.start)}${d.days > 1 ? ` – ${fmtShort(d.finish)}` : ''} on ${d.machine}`
            + (d.delivery_week ? `, due Wk ${d.delivery_week}` : ''),
      note: d.same_part ? 'Same part — batching the two runs may suit better than moving one' : null,
      dayPlan: d.day_plan || [],
      movedDays: d.days || 0,
      ladder: [{ label: `move ${weekName(d)}`, qty: requested_qty - (d.remaining_shortfall || 0) }],
      steps: d.resolves ? [moveStep(d)] : null,
      planShifts: 1,
      facts: [
        { label: 'Due', value: d.delivery_week ? `Wk ${d.delivery_week}` : 'no week set' },
        { label: 'Order', value: d.order_number },
        { label: 'Machine', value: d.machine },
        { label: 'Runs now', value: `${fmtShort(d.start)}${d.days > 1 ? ` – ${fmtShort(d.finish)}` : ''}` },
        { label: 'Moves to', value: restartOf(d) ? fmtShort(restartOf(d)) : null, hi: true },
        { label: 'Slack', value: d.undated ? 'unlimited' : `${d.slack_days} days` },
      ],
      cascade: cascadeLine(d.cascade, weekName(d)),
      safe: d.cascade ? d.cascade.ok !== false : true,
      gantt: {
        planDates: d.plan_dates || [],
        displacedPlanId: d.plan_id,
        moved: d.cascade?.moved || [],
        planShifts: d.combined_with_second_shift ? 2 : 1,
      },
      outcome: d.resolves
        ? `Your order then finishes ${fmtDate(d.new_finish)}`
        : d.makes_quantity
          ? `All of it fits, but finishes ${fmtDate(d.new_finish)} — still late`
          : `${nf(d.remaining_shortfall)} parts would still not fit`,
    });
  }

  if (ss?.considered && ss.helps) {
    const nights = (ss.dates || []).length;
    options.push({
      key: 'shift',
      icon: 'fa-clock',
      resolves: ss.makes_quantity !== false && ss.meets_target === true,
      title: `Add an ${addedShiftName(2)} shift`,
      price: `${nights} ${addedShiftName(2)} shift${nights === 1 ? '' : 's'} · ${dateRanges(ss.dates)}`,
      costSays: ss.cost ? ss.cost.says : null,
      declare: (ss.machine_ids || []).length && (ss.dates || []).length
        ? { machineIds: ss.machine_ids, machines: ss.machines || [], dates: ss.dates, cost: ss.cost || null }
        : null,
      detail: `Only this order runs a second shift. Back to mornings only after ${fmtShort(ss.to)} — nothing already on the board changes.`,
      facts: [
        { label: 'Nights', value: `${nights}` },
        { label: 'Dates', value: dateRanges(ss.dates) },
        { label: 'Machine', value: (ss.machines || []).join(', ') },
      ],
      strip: { plan: ss.dates || [], displaced: [], shifts: 2 },
      dayPlan: ss.day_plan || [],
      ladder: [{ label: `add ${addedShiftName(2)} shifts`,
                 qty: ss.planned_qty ?? requested_qty }],
      steps: (ss.makes_quantity !== false && ss.meets_target === true)
        ? [{ verb: 'Run', what: `your order on ${shiftListName(2)} shifts`,
             where: (ss.machines || []).join(', '),
             when: `${dateRanges(ss.dates)} · ${nights} day${nights === 1 ? '' : 's'} with an ${addedShiftName(2)} shift` }]
        : null,
      outcome: ss.verdict === 'reduces_shortfall'
        ? `Gets to ${nf(ss.planned_qty)} parts — still ${nf(ss.shortfall)} short`
        : ss.meets_target === true
          ? `Finishes ${fmtDate(ss.finish)}, inside your delivery week`
          : `Finishes ${fmtDate(ss.finish)}${ss.weeks_late ? ` — ${ss.weeks_late} week${ss.weeks_late === 1 ? '' : 's'} late` : ''}`,
    });
  }

  if (alt?.primary_only_was_on) {
    options.push({
      key: 'alt-machines',
      icon: 'fa-shuffle',
      resolves: false, untested: true,
      title: 'Allow alternative machines',
      price: 'Free — this was your own constraint',
      detail: "You asked for the primary machine only. Unticking that lets this part's other machines be considered.",
      outcome: 'Not checked yet — untick it and run again',
      steps: [{ verb: 'Untick', what: '“Consider only the primary machine”', when: 'then check again' }],
    });
  }

  if (alt?.partial_now) {
    options.push({
      key: 'partial',
      icon: 'fa-scissors',
      resolves: false,
      title: `Deliver ${nf(alt.partial_now.qty)} on time, the rest later`,
      price: `Customer waits for ${nf(requested_qty - alt.partial_now.qty)} parts`,
      detail: `The board can make ${nf(alt.partial_now.qty)} of ${nf(requested_qty)} by ${fmtDate(alt.partial_now.by)}.`,
      outcome: 'A split delivery worth putting to the customer',
      steps: [{ verb: 'Agree', what: `${nf(alt.partial_now.qty)} for the delivery week`,
                when: `the remaining ${nf(requested_qty - alt.partial_now.qty)} to follow` }],
    });
  }

  if (alt?.earliest_full) {
    const f = alt.earliest_full;
    options.push({
      key: 'later',
      icon: 'fa-calendar-check',
      resolves: false,
      title: `Move the delivery to Wk ${f.week}`,
      price: 'Customer waits',
      detail: `The full ${nf(requested_qty)} takes ${f.working_days} day${f.working_days === 1 ? '' : 's'} on ${f.machines.join(', ')}.`,
      outcome: `Finishes ${fmtDate(f.finish)}`,
      steps: [{ verb: 'Agree', what: `a Wk ${f.week} delivery`, when: 'with the customer' }],
    });
  }

  // Drop a bare move when the same run, combined with an extra shift, works.
  const solvedPlans = new Set(
    (alt?.displacement || []).filter(d => d.combined_with_second_shift && d.resolves).map(d => d.plan_id));
  const visible = options.filter(o => !(o.key.startsWith('disp-') && solvedPlans.has(o.key.slice(5))));
  options.length = 0;
  options.push(...visible);

  const primary = options.find(o => o.resolves && o.safe !== false)
               || options.find(o => o.resolves)
               || null;

  // A plan that fits as asked.
  if (feasible && !partial) {
    if (meets_target === false) {
      return {
        tone: AMBER, icon: 'fa-clock', kind: 'late',
        headline: `Not by your date — ${weeks_late} week${weeks_late === 1 ? '' : 's'} late`,
        sub: `The whole order can be made, but the earliest finish is ${fmtDate(finish)} (Wk ${earliest_week}).`,
        options, primary,
      };
    }
    if (at_risk) {
      return {
        tone: AMBER, icon: 'fa-triangle-exclamation', kind: 'tight',
        headline: 'Yes — but with no slack',
        sub: `All ${nf(requested_qty)} finish ${fmtDate(finish)} (Wk ${earliest_week}), with some of it in the grace period before the lorry. One hold-up and the delivery is at risk.`,
        options, primary, canHold: true,
      };
    }
    return {
      tone: GREEN, icon: 'fa-circle-check', kind: 'yes',
      headline: 'Yes, we can take it',
      sub: `All ${nf(requested_qty)} finish ${fmtDate(finish)} (Wk ${earliest_week}), comfortably inside your delivery week.`,
      options, primary, canHold: true,
    };
  }

  // It does not fit as asked — but something makes it work.
  if (primary) {
    const n = (primary.steps || []).length || 1;
    const count = n === 1 ? 'one change' : n === 2 ? 'two changes' : `${n} changes`;
    const when = askedWeek ? `Wk ${askedWeek}` : `Wk ${primary.finishWeek || earliest_week}`;
    return {
      tone: TEAL, icon: 'fa-circle-check', kind: 'conditional',
      // Solution first. "The board cannot fit it" opened on a negative, and
      // nobody reads past that to find the yes.
      headline: `Yes — with ${count}`,
      sub: `The full ${nf(requested_qty)}${partName ? ` ${partName}` : ''} can be delivered in ${when}.`,
      options, primary,
    };
  }

  // Nothing reaches the date, but the options still say what is possible.
  if (options.length > 0) {
    return {
      tone: AMBER, icon: 'fa-circle-exclamation', kind: 'compromise',
      headline: 'Not as asked — but here are the trades',
      sub: partial
        ? `Only ${nf(planned_qty)} of ${nf(requested_qty)} fit before your date, and nothing free makes the rest fit. Something has to give.`
        : reason,
      options, primary: null,
    };
  }

  return {
    tone: RED, icon: 'fa-circle-xmark', kind: 'no',
    headline: 'No — the board cannot fit this',
    sub: reason,
    options: [], primary: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────

export default function CapacityCheckModal({ onClose, initialPartId = null }) {
  const [parts, setParts]     = useState([]);
  const [partId, setPartId]   = useState(initialPartId || '');
  const [qty, setQty]         = useState('');
  const [week, setWeek]       = useState('');
  const [year, setYear]       = useState(new Date().getFullYear());
  const [primaryOnly, setPrimaryOnly] = useState(false);
  // Plan at the cycle the floor achieves, where it has the evidence (utils/floorCycle).
  const [floorCycle, setFloorCycle] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);
  const [editing, setEditing] = useState(true);

  const [holding, setHolding]   = useState(false);
  const [held, setHeld]         = useState(null);
  const [holdDays, setHoldDays] = useState(5);
  const [holdNote, setHoldNote] = useState('');

  useEffect(() => {
    api.get('/api/v1/parts')
      .then(r => setParts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setParts([]));
  }, []);

  // A new question invalidates the old answer. Leaving a verdict on screen while
  // the inputs say something else is how someone acts on the wrong "yes".
  useEffect(() => {
    setResult(null); setError(null); setHeld(null);
  }, [partId, qty, week, year, primaryOnly]);

  const activeParts = useMemo(
    () => (parts || []).filter(p => !p.discontinued)
                       .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [parts]);

  const selectedPart = useMemo(
    () => activeParts.find(p => String(p.id) === String(partId)) || null,
    [activeParts, partId]);
  const partName = selectedPart?.name || '';

  async function run(floorOverride) {
    const useFloor = typeof floorOverride === 'boolean' ? floorOverride : floorCycle;
    setLoading(true); setError(null); setResult(null); setHeld(null);
    try {
      const { data } = await api.post('/api/v1/orders/schedule/capacity-check', {
        part_id: Number(partId),
        quantity: Number(qty),
        delivery_week: week ? Number(week) : null,
        delivery_year: week ? Number(year) : null,
        force_primary: primaryOnly,
        floor_cycle: useFloor,
      });
      setResult(data);
      setEditing(false);   // the answer earns the space once there is one
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Check failed');
    } finally {
      setLoading(false);
    }
  }

  async function hold() {
    setHolding(true); setError(null);
    try {
      const { data } = await api.post('/api/v1/orders/schedule/hold', {
        part_id: Number(partId),
        quantity: Number(qty),
        delivery_week: week ? Number(week) : null,
        delivery_year: week ? Number(year) : null,
        force_primary: primaryOnly,
        floor_cycle: floorCycle,
        expires_in_days: Number(holdDays) || 5,
        note: holdNote.trim() || null,
      });
      setHeld(data);
    } catch (e) {
      setError(e.response?.data?.reason || e.response?.data?.error || 'Could not hold these days');
    } finally {
      setHolding(false);
    }
  }

  const canRun = partId && Number(qty) >= 1 && !loading;
  const answer = result ? decide(result, partName, week) : null;

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.card} onClick={e => e.stopPropagation()}>
        <div style={st.head}>
          <div style={st.title}>
            <i className="fa-solid fa-clipboard-question" style={{ color: TEAL }} />
            Can we take this order?
          </div>
          <button aria-label="Close" style={{ background: 'none', border: 'none', color: MUTED, fontSize: 16, cursor: 'pointer' }} onClick={onClose}>✕</button>
        </div>

        <div style={st.body}>
          {editing ? (
            <Form {...{ activeParts, partId, setPartId, qty, setQty, week, setWeek,
                        year, setYear, primaryOnly, setPrimaryOnly, selectedPart }} />
          ) : (
            <Recap partName={partName} qty={qty} week={week} year={year}
                   primaryOnly={primaryOnly} onEdit={() => setEditing(true)} />
          )}

          {error && (
            <div style={{ marginTop: 16, padding: '13px 15px', background: `${RED}12`, border: `1px solid ${RED}44`, borderRadius: 10, display: 'flex', gap: 11 }}>
              <i className="fa-solid fa-circle-xmark" style={{ color: RED, marginTop: 2 }} />
              <div style={{ fontSize: 13, color: DIM, lineHeight: 1.6 }}>
                <div style={{ color: RED, fontWeight: 700, marginBottom: 2 }}>Something went wrong</div>
                {error}
              </div>
            </div>
          )}

          {result && result.floor && (
            <label title={result.floor.says}
              style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, border: `1px solid ${floorCycle ? TEAL : BORDER}`, background: floorCycle ? `${TEAL}10` : 'transparent', cursor: 'pointer', fontSize: 13, color: DIM, lineHeight: 1.5 }}>
              <input type="checkbox" checked={floorCycle} disabled={loading}
                onChange={e => { setFloorCycle(e.target.checked); run(e.target.checked); }} style={{ marginTop: 3, accentColor: TEAL }} />
              <span>
                <b style={{ color: TEXT }}>Plan at the floor's {result.floor.floor_cycle_s} s</b> instead of the {result.floor.planned_cycle_s} s on the part.{' '}
                The floor runs it {result.floor.diff_pct}% {result.floor.direction} (the middle of {result.floor.runs} timed runs).
              </span>
            </label>
          )}

          {answer && (
            <Answer answer={answer} result={result} held={held} holding={holding}
                    onHold={hold} holdDays={holdDays} setHoldDays={setHoldDays}
                    holdNote={holdNote} setHoldNote={setHoldNote} />
          )}
        </div>

        <div style={st.footer}>
          <button style={st.btnGhost} onClick={onClose}>Close</button>
          <button style={{ ...st.btnPrim, opacity: canRun ? 1 : 0.4, cursor: canRun ? 'pointer' : 'not-allowed' }}
                  disabled={!canRun} onClick={run}>
            {loading ? 'Checking…' : result ? 'Check again' : 'Check capacity'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── the question ────────────────────────────────────────────────────────────

function Form({ activeParts, partId, setPartId, qty, setQty, week, setWeek,
                year, setYear, primaryOnly, setPrimaryOnly, selectedPart }) {
  const readiness = partReadiness(selectedPart);
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 132px', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={st.label} htmlFor="cc-part">Part</label>
          <select id="cc-part" style={st.input} value={partId} onChange={e => setPartId(e.target.value)}>
            <option value="">Select a part…</option>
            {activeParts.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}{p.part_number ? ` · ${p.part_number}` : ''}
                {partReadiness(p).ready ? '' : '  — needs setup'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={st.label} htmlFor="cc-qty">Quantity</label>
          <input id="cc-qty" style={st.input} type="number" min="1" value={qty}
                 onChange={e => setQty(e.target.value)} placeholder="e.g. 20000" />
        </div>
      </div>

      {!readiness.ready && (
        <div style={{ margin: '0 0 12px', padding: '12px 14px', background: `${AMBER}12`,
                      border: `1px solid ${AMBER}40`, borderRadius: 10, display: 'flex', gap: 11 }}>
          <i className="fa-solid fa-screwdriver-wrench" style={{ color: AMBER, fontSize: 14, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: DIM, lineHeight: 1.6 }}>
            <div style={{ color: AMBER, fontWeight: 700, marginBottom: 2 }}>
              This part can't be scheduled yet
            </div>
            It has no {listAnd(readiness.missing)}, so the answer will be the same
            whatever the board looks like.
            {selectedPart && (
              <a href={`/admin/parts/${selectedPart.id}`} target="_blank" rel="noreferrer"
                 style={{ color: AMBER, display: 'inline-block', marginTop: 6, fontWeight: 600 }}>
                Set it up → {selectedPart.name}
              </a>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={st.label} htmlFor="cc-week">Delivery week</label>
          <input id="cc-week" style={st.input} type="number" min="1" max="53" value={week}
                 onChange={e => setWeek(e.target.value)} placeholder="Blank = earliest" />
        </div>
        <div>
          <label style={st.label} htmlFor="cc-year">Year</label>
          <input id="cc-year" style={st.input} type="number" value={year} onChange={e => setYear(e.target.value)} />
        </div>
      </div>

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginTop: 14, cursor: 'pointer', fontSize: 13, color: DIM, lineHeight: 1.5 }}>
        <input type="checkbox" checked={primaryOnly} onChange={e => setPrimaryOnly(e.target.checked)}
               style={{ accentColor: TEAL, width: 15, height: 15, marginTop: 1, flexShrink: 0, cursor: 'pointer' }} />
        <span>
          Consider only the primary machine
          <span style={{ display: 'block', fontSize: 12, color: MUTED }}>
            Ignores alternatives. Asks “can the machine this part belongs on do it?”
          </span>
        </span>
      </label>
    </>
  );
}

// Once there is an answer the question shrinks to a line so the answer gets the
// room. It stays on screen because a verdict without its question is a trap.
function Recap({ partName, qty, week, year, primaryOnly, onEdit }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  padding: '10px 14px', background: SUNK, border: `1px solid ${BORDER}`, borderRadius: 9 }}>
      <div style={{ fontSize: 13, color: TEXT, fontWeight: 600 }}>
        {partName || 'Part'}
        <span style={{ color: MUTED, fontWeight: 400 }}> · </span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{nf(qty)}</span>
        <span style={{ color: MUTED, fontWeight: 400 }}> · </span>
        {week ? `Wk ${week} ${year}` : 'earliest possible'}
      </div>
      {primaryOnly && (
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                       padding: '2px 7px', borderRadius: 4, background: `${AMBER}18`, color: AMBER, border: `1px solid ${AMBER}44` }}>
          Primary only
        </span>
      )}
      <button onClick={onEdit} style={{ ...st.btnGhost, marginLeft: 'auto', padding: '4px 11px', fontSize: 12 }}>
        Change
      </button>
    </div>
  );
}

// ── the answer ──────────────────────────────────────────────────────────────

function Answer({ answer, result, held, holding, onHold, holdDays, setHoldDays, holdNote, setHoldNote }) {
  const { tone, icon, headline, sub, options, primary, blockers = [], canHold, kind } = answer;
  const others  = options.filter(o => o !== primary);
  const works   = others.filter(o => o.resolves);
  const shortOf = others.filter(o => !o.resolves);

  return (
    <div style={{ marginTop: 18 }}>
      {/* The verdict. One thing, unmissable. */}
      <div style={{ padding: '18px 20px', background: `${tone}12`, border: `1px solid ${tone}40`, borderRadius: 12 }}>
        <div style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
          <i className={`fa-solid ${icon}`} style={{ color: tone, fontSize: 21, marginTop: 2 }} />
          <div>
            <div style={{ color: tone, fontWeight: 700, fontSize: 19, letterSpacing: '-0.01em', lineHeight: 1.25 }}>
              {headline}
            </div>
            <div style={{ color: DIM, fontSize: 13.5, lineHeight: 1.6, marginTop: 6 }}>{sub}</div>
            {!primary && result.requested_qty > 0 && result.partial && (
              <QuantityBar made={result.planned_qty} asked={result.requested_qty} tone={tone} />
            )}
          </div>
        </div>
      </div>

      {blockers.length > 0 && <Blockers blockers={blockers} partId={result.part_id} />}

      {primary && <Option option={primary} tone={TEAL} recommended result={result} />}

      {works.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ ...st.eyebrow, marginBottom: 9 }}>
            {primary ? 'Or you could' : 'What you could do'}
          </div>
          {works.map(o => <Option key={o.key} option={o} tone={TEAL} result={result} />)}
        </div>
      )}

      {shortOf.length > 0 && (
        primary || works.length > 0
          ? <ShortOf options={shortOf} result={result} />
          : (
            <div style={{ marginTop: 20 }}>
              <div style={{ ...st.eyebrow, marginBottom: 9 }}>What you could do</div>
              {shortOf.map(o => <Option key={o.key} option={o} tone={AMBER} result={result} />)}
            </div>
          )
      )}

      {canHold && (
        <HoldPanel held={held} holding={holding} onHold={onHold} result={result}
                   holdDays={holdDays} setHoldDays={setHoldDays}
                   holdNote={holdNote} setHoldNote={setHoldNote} />
      )}

      {kind !== 'setup' && <Evidence result={result} primary={primary} />}
    </div>
  );
}

function Blockers({ blockers, partId }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ ...st.eyebrow, marginBottom: 8 }}>What needs fixing</div>
      {blockers.map((b, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, fontSize: 13, color: DIM, lineHeight: 1.6, padding: '4px 0' }}>
          <i className="fa-solid fa-wrench" style={{ color: AMBER, marginTop: 3, fontSize: 12 }} />
          <span>{b}</span>
        </div>
      ))}
      {partId ? (
        <a href={`/admin/parts/${partId}`} target="_blank" rel="noreferrer"
           style={{ ...st.btnGhost, color: AMBER, borderColor: `${AMBER}55`, display: 'inline-flex',
                    alignItems: 'center', gap: 7, marginTop: 11, textDecoration: 'none', padding: '7px 13px' }}>
          <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: 11 }} />
          Open this part and set it up
        </a>
      ) : (
        <div style={{ fontSize: 12, color: MUTED, marginTop: 9 }}>
          Set this up in Admin → Parts, then check again.
        </div>
      )}
    </div>
  );
}

// One way forward, with what it costs. The price is the point — it is what the
// director is choosing between, and the thing a schedule normally hides.
// An option that works reads as an instruction; one that does not reads as a
// trade. Either way the working is folded away — a director acting on the
// answer never needs it, and one doubting the answer is a click from all of it.
function Option({ option, tone, recommended = false, result }) {
  const [open, setOpen] = useState(false);
  const { icon, title, price, detail, outcome, note, untested,
          facts, strip, cascade, gantt, steps, costSays, declare } = option;

  const reassurance = cascade
    ? (cascade.safe ? 'nothing else affected' : 'affects other deliveries')
    : null;

  return (
    <div style={{
      marginTop: recommended ? 18 : 0, marginBottom: recommended ? 0 : 8,
      background: recommended ? `${tone}0e` : SUNK,
      border: `1px solid ${recommended ? `${tone}44` : BORDER}`,
      borderRadius: 11, overflow: 'hidden',
    }}>
      {recommended && (
        <div style={{ ...st.eyebrow, color: tone, background: `${tone}18`, padding: '5px 15px' }}>
          Recommended — do this
        </div>
      )}

      <div style={{ padding: '13px 15px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: `${tone}1c`, border: `1px solid ${tone}3a`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
            <i className={`fa-solid ${icon}`} style={{ color: tone, fontSize: 12 }} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Recommended options lead with what to do; the title would only
                restate the steps. Everything else still needs naming. */}
            {!(recommended && steps) && (
              <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, lineHeight: 1.35 }}>{title}</div>
            )}

            {steps ? <Steps steps={steps} tone={tone} /> : (
              <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.55, marginTop: 5 }}>{outcome}</div>
            )}

            <div style={{ fontSize: 12.5, color: tone, fontWeight: 600, marginTop: steps ? 11 : 4 }}>
              {price}
              {reassurance && <span style={{ color: cascade.safe ? MUTED : RED }}> · {reassurance}</span>}
            </div>

            {untested && (
              <div style={{ fontSize: 12, color: MUTED, marginTop: 5 }}>
                <i className="fa-solid fa-circle-question" style={{ marginRight: 6 }} />{outcome}
              </div>
            )}
            {costSays && (
              <div style={{ fontSize: 12, color: DIM, marginTop: 5, lineHeight: 1.5 }}>
                <i className="fa-solid fa-coins" style={{ color: AMBER, marginRight: 6, fontSize: 10 }} />{costSays}
              </div>
            )}
            {declare && <DeclareWindow d={declare} result={result} />}
          </div>

          <button onClick={() => setOpen(!open)}
            style={{ background: 'none', border: `1px solid ${BORDER}`, color: MUTED, borderRadius: 7,
                     padding: '4px 10px', fontSize: 11.5, cursor: 'pointer', flexShrink: 0,
                     display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start' }}>
            Details <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 8 }} />
          </button>
        </div>

        {open && (
          <div style={{ marginTop: 13, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
            {facts && facts.length > 0 && <Facts facts={facts} />}
            {detail && <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.55, marginTop: 9 }}>{detail}</div>}
            {gantt && <Gantt {...gantt} deadline={result?.target_date} />}
            {strip && <Strip {...strip} tone={tone} deadline={result?.target_date} />}
            {cascade && (
              <div style={{ display: 'flex', gap: 8, marginTop: 11, padding: '9px 11px', borderRadius: 7,
                            background: cascade.safe ? `${TEAL}0e` : `${RED}12`,
                            border: `1px solid ${cascade.safe ? `${TEAL}33` : `${RED}44`}` }}>
                <i className={`fa-solid ${cascade.safe ? 'fa-circle-check' : 'fa-triangle-exclamation'}`}
                   style={{ color: cascade.safe ? TEAL : RED, fontSize: 11, marginTop: 3 }} />
                <div style={{ fontSize: 12.5, color: cascade.safe ? DIM : '#fca5a5', lineHeight: 1.55 }}>
                  {cascade.text}
                </div>
              </div>
            )}
            {note && (
              <div style={{ fontSize: 12, color: TEAL, marginTop: 9, display: 'flex', gap: 6 }}>
                <i className="fa-solid fa-lightbulb" style={{ marginTop: 3, fontSize: 10 }} />{note}
              </div>
            )}
            <div style={{ fontSize: 12.5, color: MUTED, marginTop: 10, paddingTop: 9, borderTop: `1px solid ${BORDER}` }}>
              {outcome}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Numbered because the order matters: move the run, then book the nights.
function Steps({ steps, tone }) {
  return (
    <ol style={{ margin: '7px 0 0', padding: 0, listStyle: 'none', display: 'flex',
                 flexDirection: 'column', gap: 7 }}>
      {steps.map((s, i) => (
        <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ width: 17, height: 17, borderRadius: '50%', background: `${tone}22`,
                         border: `1px solid ${tone}55`, color: tone, fontSize: 10, fontWeight: 700,
                         display: 'flex', alignItems: 'center', justifyContent: 'center',
                         flexShrink: 0, marginTop: 2 }}>{i + 1}</span>
          <span style={{ fontSize: 13.5, color: TEXT, lineHeight: 1.45 }}>
            <strong style={{ color: tone }}>{s.verb}</strong> {s.what}
            {s.where && <span style={{ color: MUTED }}> on {s.where}</span>}
            {(s.when || s.highlight) && (
              <span style={{ display: 'block', fontSize: 12, color: DIM, marginTop: 3 }}>
                {s.whenPre && <>{s.whenPre} </>}
                {s.highlight && (
                  <strong style={{ color: tone, background: `${tone}1c`, border: `1px solid ${tone}44`,
                                   borderRadius: 5, padding: '2px 7px', fontSize: 12.5,
                                   fontVariantNumeric: 'tabular-nums' }}>{s.highlight}</strong>
                )}
                {s.whenPost && <> {s.whenPost}</>}
                {s.when}
              </span>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

// ── reserving the days ──────────────────────────────────────────────────────

function HoldPanel({ held, holding, onHold, result, holdDays, setHoldDays, holdNote, setHoldNote }) {
  if (held) {
    return (
      <div style={{ marginTop: 18, padding: '15px 17px', background: `${TEAL}12`, border: `1px solid ${TEAL}44`, borderRadius: 11 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <i className="fa-solid fa-lock" style={{ color: TEAL, fontSize: 15, marginTop: 3 }} />
          <div style={{ fontSize: 13, color: DIM, lineHeight: 1.65 }}>
            <div style={{ color: TEAL, fontWeight: 700, fontSize: 14.5, marginBottom: 3 }}>
              {held.days} day{held.days === 1 ? '' : 's'} reserved
            </div>
            {fmtDate(held.start)} → {fmtDate(held.finish)}. Nothing else can take these days
            until <strong style={{ color: TEXT }}>{fmtDate(held.expires_at)}</strong>, when they
            go back automatically and you get a notification.
            <div style={{ fontSize: 12, color: MUTED, marginTop: 7 }}>
              Showing on the schedule as a held run — release it there if the order falls through.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 20, paddingTop: 18, borderTop: `1px solid ${BORDER}` }}>
      <div style={{ ...st.eyebrow, marginBottom: 8 }}>Before you ring back</div>
      <div style={{ fontSize: 13, color: DIM, lineHeight: 1.6, marginBottom: 13 }}>
        Reserve {result.working_days} day{result.working_days === 1 ? '' : 's'} on {(result.machines || []).join(', ')} so
        nothing else takes them. No order is created and no work reaches the floor.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 132px', gap: 12, marginBottom: 13 }}>
        <div>
          <label style={st.label} htmlFor="cc-who">Who for</label>
          <input id="cc-who" style={st.input} value={holdNote} maxLength={80}
                 onChange={e => setHoldNote(e.target.value)} placeholder="Customer or enquiry ref" />
        </div>
        <div>
          <label style={st.label} htmlFor="cc-days">Hold for</label>
          <select id="cc-days" style={st.input} value={holdDays} onChange={e => setHoldDays(Number(e.target.value))}>
            <option value={2}>2 days</option>
            <option value={5}>5 days</option>
            <option value={10}>10 days</option>
            <option value={20}>20 days</option>
          </select>
        </div>
      </div>

      <button onClick={onHold} disabled={holding}
        style={{ ...st.btnPrim, width: '100%', padding: '11px 20px', opacity: holding ? 0.6 : 1,
                 cursor: holding ? 'wait' : 'pointer', display: 'flex', alignItems: 'center',
                 justifyContent: 'center', gap: 8 }}>
        <i className="fa-solid fa-lock" style={{ fontSize: 12 }} />
        {holding ? 'Holding…' : `Hold ${fmtShort(result.start)} – ${fmtShort(result.finish)}`}
      </button>
      <div style={{ fontSize: 12, color: MUTED, marginTop: 8, lineHeight: 1.5 }}>
        The plan is worked out again when you press this, so the days reserved are the
        ones free right now.
      </div>
    </div>
  );
}

// ── the working ─────────────────────────────────────────────────────────────
// Closed by default. A director acting on the answer does not need it; a
// director doubting the answer needs all of it.

// ── the working ─────────────────────────────────────────────────────────────
//
// Not a list of statistics but the arithmetic, start to finish: where the board
// stood, what each change buys, and the days that add up to the order. Every
// line is checkable against the one above it.
//
// An earlier version described the do-nothing plan under a heading promising to
// explain the answer — so it showed 6,376 in red beneath a verdict saying yes.
// The story now ends where the verdict does.

function Evidence({ result, primary }) {
  const [open, setOpen] = useState(false);
  const { search, machines = [], working_days,
          planned_qty, requested_qty, target_date } = result;

  const s = search || {};
  const freeDays    = working_days || 0;
  const bookedDays  = (s.rejected || []).reduce((n, r) => n + r.days, 0);
  const scannedDays = s.working_days_checked || 0;
  const machine     = (machines[0] || (s.machines_considered || [])[0] || 'the machine');

  // With a recommendation, the plan worth explaining is the recommended one.
  const finalPlan = primary?.dayPlan || [];
  const steps     = primary?.steps || [];

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${BORDER}` }}>
      <button onClick={() => setOpen(!open)}
        style={{ background: 'none', border: 'none', color: MUTED, fontSize: 12, fontWeight: 700,
                 letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', padding: 0,
                 display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 9 }} />
        How this was worked out
      </button>

      {open && (
        <div style={{ marginTop: 15, fontSize: 13, color: DIM, lineHeight: 1.6 }}>

          {primary?.ladder?.length > 0 && (
            <Stanza title={`Getting to ${nf(requested_qty)}`}>
              <Ladder base={planned_qty} rungs={primary.ladder} asked={requested_qty} />
            </Stanza>
          )}

          <Stanza title="Starting point">
            <div><strong style={{ color: TEXT }}>{machine}</strong> has {scannedDays} working day{scannedDays === 1 ? '' : 's'}
              {target_date && <> before the production cut-off, <strong style={{ color: TEXT }}>{fmtDate(target_date)}</strong></>}.
            </div>
            {bookedDays > 0 && (
              <Line label={`${bookedDays} already booked`}
                    note={(s.rejected || []).map(r => r.reason.toLowerCase()).join('; ')} />
            )}
            <Line label={`${freeDays} free`}
                  note={`which make ${nf(planned_qty)} of the ${nf(requested_qty)} you need`} />
          </Stanza>

          {steps.map((st2, i) => (
            <Stanza key={i} title={`Change ${i + 1} · ${st2.verb.toLowerCase()} ${st2.what}`}>
              <div>{stepGain(st2, i, primary, freeDays, s)}</div>
            </Stanza>
          ))}

          {/* Only the plan the changes actually produce. Falling back to the
              baseline here would label the rejected scenario as the result. */}
          {primary && finalPlan.length > 0 && (
            <Stanza title="Which gives you">
              <DayTable plan={finalPlan} asked={requested_qty} mouldMin={s.mould_change_min} />
            </Stanza>
          )}

          {s.parts_per_day != null && (
            <div style={{ fontSize: 12, color: MUTED, marginTop: 14, paddingTop: 11,
                          borderTop: `1px solid ${BORDER}`, lineHeight: 1.6 }}>
              This part runs <strong style={{ color: DIM }}>{nf(s.parts_per_day)}</strong> a day on {SHIFT_NAMES[0]}s
              {s.parts_per_day_two_shifts ? <>, <strong style={{ color: DIM }}>{nf(s.parts_per_day_two_shifts)}</strong> with {SHIFT_NAMES[1]}s added</> : null}.
              {s.forced_primary && <> Alternatives were excluded at your request.</>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stanza({ title, children }) {
  return (
    <div style={{ marginBottom: 15 }}>
      <div style={{ ...st.eyebrow, color: MUTED, marginBottom: 5 }}>{title}</div>
      {children}
    </div>
  );
}

function Line({ label, note }) {
  return (
    <div style={{ display: 'flex', gap: 9, marginTop: 3 }}>
      <span style={{ color: TEXT, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                     minWidth: 96, flexShrink: 0 }}>{label}</span>
      {note && <span style={{ color: MUTED, fontSize: 12.5 }}>{note}</span>}
    </div>
  );
}

// What each change buys, in the same units as the line above it.
function stepGain(step, i, primary, freeDays, search) {
  if (step.verb === 'Move') {
    const freed = primary?.movedDays || 0;
    return freed
      ? <>Frees {freed} day{freed === 1 ? '' : 's'} → <strong style={{ color: TEXT }}>{freeDays + freed} days free</strong></>
      : <>Opens the days it was occupying.</>;
  }
  if (step.verb === 'Run') {
    return search.parts_per_day_two_shifts
      ? <>Each day makes <strong style={{ color: TEXT }}>{nf(search.parts_per_day_two_shifts)}</strong> instead of {nf(search.parts_per_day)}.</>
      : <>Adds an {SHIFT_NAMES[1]} shift to each day of the run.</>;
  }
  return <>{step.when || step.whenPost || ''}</>;
}

function fmtMins(m) {
  const h = Math.floor(m / 60), r = m % 60;
  return h ? `${h}h ${r}m` : `${r}m`;
}

// enough to act on when three runs of that part sit on the board — the order
// number, the machine and the dates are what identify the one meant.
function Facts({ facts }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 8px', marginTop: 9 }}>
      {facts.filter(f => f.value).map(f => (
        <span key={f.label} style={{ fontSize: 11.5, color: DIM,
              background: f.hi ? `${TEAL}14` : '#11151a',
              border: `1px solid ${f.hi ? `${TEAL}44` : BORDER}`,
              borderRadius: 5, padding: '3px 8px', whiteSpace: 'nowrap' }}>
          <span style={{ color: f.hi ? TEAL : MUTED }}>{f.label} </span>
          <span style={{ color: f.hi ? TEAL : TEXT, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{f.value}</span>
        </span>
      ))}
    </div>
  );
}

// A schedule question deserves a schedule. One calendar day per cell across the
// window that matters: the run being proposed, the run being moved out of the
// way, and the last day production can happen. Weekends stay visible but dim,
// because "why is there a gap there" is a question the strip should answer.
function Strip({ plan = [], displaced = [], displacedLabel, tone, deadline }) {
  const all = [...plan, ...displaced, deadline].filter(Boolean).sort();
  if (all.length === 0) return null;

  const from = all[0];
  const to   = all[all.length - 1];
  const span = daysBetweenStr(from, to);
  if (span < 0 || span > 120) return null;          // nothing sensible to draw

  const planSet = new Set(plan);
  const dispSet = new Set(displaced);
  const cells = [];
  for (let i = 0; i <= span; i++) {
    const d = addDaysStr(from, i);
    cells.push({
      d,
      weekend: isWeekendStr(d),
      mine: planSet.has(d),
      theirs: dispSet.has(d),
      deadline: d === deadline,
    });
  }

  const monthLabel = x => new Date(x + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  return (
    <div style={{ marginTop: 11 }}>
      <div style={{ display: 'flex', gap: 2, alignItems: 'stretch', height: 26 }}>
        {cells.map(c => (
          <div key={c.d} title={`${fmtDate(c.d)}${c.mine ? ' — your order' : c.theirs ? ` — ${displacedLabel || 'moves'}` : c.deadline ? ' — production cut-off, the lorry loads the next day' : ''}`}
            style={{
              flex: 1, minWidth: 3, borderRadius: 2, position: 'relative',
              background: c.mine ? tone
                        : c.theirs ? `${AMBER}55`
                        : c.weekend ? '#14181e' : '#1c2128',
              border: c.deadline ? `1px solid ${RED}` : '1px solid transparent',
              opacity: c.mine || c.theirs ? 1 : 0.9,
            }}>
            {c.theirs && !c.mine && (
              <div style={{ position: 'absolute', inset: 0, borderRadius: 2,
                            border: `1px dashed ${AMBER}`, boxSizing: 'border-box' }} />
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 10.5, color: MUTED }}>
        <span>{monthLabel(from)}</span>
        {deadline && <span style={{ color: RED }}>cut-off {monthLabel(deadline)}</span>}
        <span>{monthLabel(to)}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 7, fontSize: 10.5, color: MUTED }}>
        <Key colour={tone} label="your order" />
        {displaced.length > 0 && <Key colour={`${AMBER}55`} dashed label={`${displacedLabel || 'run'} moves out`} />}
      </div>
    </div>
  );
}

function Key({ colour, label, dashed }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <i style={{ width: 11, height: 11, borderRadius: 2, background: colour,
                  border: dashed ? `1px dashed ${AMBER}` : 'none', display: 'inline-block' }} />
      {label}
    </span>
  );
}

// How much of the order actually gets made. A number against a number is hard
// to feel; a bar is immediate.
function QuantityBar({ made, asked, tone }) {
  if (!asked) return null;
  const pct = Math.min(100, Math.round((made / asked) * 100));
  return (
    <div style={{ marginTop: 13 }}>
      <div style={{ height: 8, background: '#11151a', borderRadius: 4, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
        <div style={{ width: `${pct}%`, height: '100%', background: tone, borderRadius: 3 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
        <span style={{ color: tone, fontWeight: 700 }}>{nf(made)} made</span>
        <span style={{ color: MUTED }}>{pct}% of {nf(asked)}</span>
      </div>
    </div>
  );
}

// ── before and after ────────────────────────────────────────────────────────
//
// A move is a change, and one row can only draw a state. So the board is shown
// twice: as it stands, and as it would be. The run being moved, every run the
// push shoves along behind it, and the new order all appear in both, which is
// what makes the gap visible rather than described.
//
// Positions come from the cascade walk, so the second row is arithmetic rather
// than an artist's impression.

const BAR = {
  mine:      TEAL,          // the order being asked about
  displaced: AMBER,         // the run you are being asked to move
  knock:     '#3f4a5a',     // runs that shift only because that one did
  broken:    RED,           // a run whose delivery no longer holds
};

function Gantt({ planDates = [], deadline, displacedPlanId, moved = [], planShifts = 1 }) {
  const [hover, setHover] = useState(null);

  const bars = useMemo(() => {
    const now = [], after = [];

    for (const m of moved) {
      const isDisplaced = m.plan_id === displacedPlanId;
      const label = `${m.delivery_week ? `Wk ${m.delivery_week} ` : ''}${m.part}`;
      const colour = m.late_by ? BAR.broken : isDisplaced ? BAR.displaced : BAR.knock;
      now.push({ id: `n-${m.plan_id}`, from: m.start, to: m.finish, colour, label,
                 tip: `${label} · ${fmtShort(m.start)} – ${fmtShort(m.finish)}`
                    + (m.delivery_week ? ` · due Wk ${m.delivery_week}` : '') });
      after.push({ id: `a-${m.plan_id}`, from: m.new_start, to: m.new_finish, colour, label,
                   tip: `${label} · moves to ${fmtShort(m.new_start)} – ${fmtShort(m.new_finish)}`
                      + (m.late_by ? ` · ${m.late_by} days LATE` : m.slack_left != null ? ` · ${m.slack_left} days spare` : ''),
                   moved: true });
    }

    if (planDates.length > 0) {
      after.push({
        id: 'a-mine', from: planDates[0], to: planDates[planDates.length - 1],
        colour: BAR.mine, label: 'Your order', mine: true, shifts: planShifts,
        tip: `Your order · ${fmtShort(planDates[0])} – ${fmtShort(planDates[planDates.length - 1])}`,
      });
    }
    return { now, after };
  }, [planDates, displacedPlanId, moved, planShifts]);

  const all = [...bars.now, ...bars.after];
  if (all.length === 0) return null;

  // Window: everything drawn, plus the deadline, padded to whole weeks so the
  // gridlines land on Mondays.
  const edges = all.flatMap(b => [b.from, b.to]).concat(deadline ? [deadline] : []).filter(Boolean).sort();
  let from = edges[0], to = edges[edges.length - 1];
  while (new Date(from + 'T12:00:00').getDay() !== 1) from = addDaysStr(from, -1);
  to = addDaysStr(to, 3);
  const span = daysBetweenStr(from, to) + 1;
  if (span < 1 || span > 400) return null;

  const pct = d => (daysBetweenStr(from, d) / span) * 100;
  const wd  = (a, b) => ((daysBetweenStr(a, b) + 1) / span) * 100;

  // Monday gridlines, labelled with their week and date.
  const weeks = [];
  for (let d = from; d <= to; d = addDaysStr(d, 7)) weeks.push(d);
  // Labels thin out on a long window rather than overprinting each other;
  // the gridlines stay at every week regardless.
  const axisStep = Math.max(1, Math.ceil(weeks.length / 9));

  const Row = ({ title, items, showDeadline }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
      <div style={{ width: 54, flexShrink: 0, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em',
                    textTransform: 'uppercase', color: MUTED, textAlign: 'right' }}>{title}</div>
      <div style={{ position: 'relative', flex: 1, height: 34, background: '#12161c',
                    borderRadius: 4, overflow: 'hidden' }}>
        {weeks.map(w => (
          <div key={w} style={{ position: 'absolute', left: `${pct(w)}%`, top: 0, bottom: 0,
                                width: 1, background: '#1e242c' }} />
        ))}
        {showDeadline && deadline && (
          <div style={{ position: 'absolute', left: `${pct(addDaysStr(deadline, 1))}%`, top: 0, bottom: 0,
                        width: 0, borderLeft: `2px dashed ${RED}`, zIndex: 3 }} />
        )}
        {items.map(b => (
          <div key={b.id}
            onMouseEnter={() => setHover(b)}
            onMouseLeave={() => setHover(h => (h && h.id === b.id ? null : h))}
            style={{
              position: 'absolute', left: `${pct(b.from)}%`, width: `${wd(b.from, b.to)}%`,
              top: 3, bottom: 3, borderRadius: 3, zIndex: 2, overflow: 'hidden',
              display: 'flex', flexDirection: 'column', gap: 1, cursor: 'default',
              outline: hover && hover.id === b.id ? `2px solid #fff6` : 'none',
            }}>
            <div style={{ flex: 1, background: b.colour, borderRadius: 2, display: 'flex',
                          alignItems: 'center', paddingLeft: 6, overflow: 'hidden',
                          fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap',
                          color: b.colour === BAR.knock ? '#c6cfdb' : '#06231f' }}>
              {wd(b.from, b.to) > 16 ? (b.shifts === 2 ? `${SHIFT_NAMES[0]} shift` : b.label) : ''}
            </div>
            {b.shifts === 2 && (
              <div style={{ flex: 1, background: b.colour, opacity: 0.55, borderRadius: 2,
                            display: 'flex', alignItems: 'center', paddingLeft: 6, overflow: 'hidden',
                            fontSize: 9.5, fontWeight: 700, whiteSpace: 'nowrap', color: '#06231f' }}>
                {wd(b.from, b.to) > 16 ? `${addedShiftName(2)} shift` : ''}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: 12, position: 'relative' }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 5 }}>
        <div style={{ width: 54, flexShrink: 0 }} />
        <div style={{ position: 'relative', flex: 1, height: 27 }}>
          {weeks.map((w, i) => (i % axisStep !== 0 ? null : (
            <div key={w} style={{ position: 'absolute', left: `${pct(w)}%`, whiteSpace: 'nowrap',
                                  paddingLeft: 3, lineHeight: 1.3 }}>
              <div style={{ fontSize: 9.5, color: MUTED, fontWeight: 700, letterSpacing: '0.04em' }}>
                Wk {isoWeekOf(w)}
              </div>
              <div style={{ fontSize: 10.5, color: DIM, fontVariantNumeric: 'tabular-nums' }}>
                {fmtShort(w)}
              </div>
            </div>
          )))}
        </div>
      </div>

      {bars.now.length > 0 && <Row title="Now" items={bars.now} />}
      <Row title="After" items={bars.after} showDeadline />

      {/* Zero-delay tooltip: a real element, since title= waits about a second
          and cannot be styled. */}
      {hover && (
        <div style={{ position: 'absolute', left: `calc(64px + ${Math.min(pct(hover.from), 70)}%)`,
                      bottom: '100%', marginBottom: 4, background: '#0b0e12', color: TEXT,
                      border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 9px',
                      fontSize: 11.5, whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.6)' }}>
          {hover.tip}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 8, fontSize: 10.5, color: MUTED }}>
        <Key colour={BAR.mine} label="your order" />
        {moved.some(m => m.plan_id === displacedPlanId) && <Key colour={BAR.displaced} label="the run you move" />}
        {moved.length > 1 && <Key colour={BAR.knock} label="shifts as a knock-on" />}
        {moved.some(m => m.late_by) && <Key colour={BAR.broken} label="misses its deadline" />}
        {planShifts === 2 && <Key colour={BAR.mine} label={`upper = ${SHIFT_NAMES[0]}, lower = ${SHIFT_NAMES[1]}`} />}
        {deadline && <span style={{ color: RED }}>┆ production cut-off</span>}
      </div>
    </div>
  );
}

// ISO week number, for the axis labels.
function isoWeekOf(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const w1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7);
}

// Approaches that were tried and do not get there. Worth keeping — they say how
// far each lever reaches on its own, which is how you judge whether the
// recommendation is doing the work. But they are not choices, so they do not
// compete with the ones that are.
function ShortOf({ options, result }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 18 }}>
      <button onClick={() => setOpen(!open)}
        style={{ background: 'none', border: 'none', color: MUTED, fontSize: 12, fontWeight: 700,
                 letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', padding: 0,
                 display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 9 }} />
        {options.length} more tried — {open ? 'hide' : 'none of them get there'}
      </button>
      {open && (
        <div style={{ marginTop: 11 }}>
          {options.map(o => <Option key={o.key} option={o} tone={AMBER} result={result} />)}
        </div>
      )}
    </div>
  );
}

// A run's days and what they make, with the total against what was asked for.
// Used twice: once for where the board stands, once for where the changes take
// it. The same shape both times, so the two are read against each other.
function DayTable({ plan, asked, mouldMin }) {
  const total = plan.reduce((n, d) => n + (Number(d.qty) || 0), 0);
  const done  = total >= asked;
  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden', marginTop: 7 }}>
      {plan.map((d, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12,
              padding: '6px 12px', fontSize: 12.5, borderBottom: `1px solid ${BORDER}` }}>
          <span>{fmtDate(d.date)}
            {i === 0 && mouldMin ? (
              <span style={{ color: MUTED }}> · {fmtMins(mouldMin)} on the mould change</span>
            ) : null}
          </span>
          <span style={{ color: TEXT, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {nf(d.qty)}
          </span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 12px',
                    background: '#11151a', fontSize: 12.5 }}>
        <span style={{ color: done ? TEAL : AMBER, fontWeight: 700 }}>
          {done ? 'order complete' : 'short of the order'}
        </span>
        <span style={{ color: done ? TEAL : AMBER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {nf(total)} of {nf(asked)}
        </span>
      </div>
    </div>
  );
}

// Where the parts come from, change by change.
//
// A director's question is not "is it possible" — the verdict answered that —
// but "where does the shortfall go?". Each row adds its change and shows the
// running total against the order, so the contribution of each is the length of
// its own bar rather than a figure to hold in your head.
function Ladder({ base, rungs, asked }) {
  const rows = [{ label: 'as the board stands', qty: base }, ...rungs];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
      {rows.map((r, i) => {
        const pct  = Math.max(2, Math.min(100, Math.round((r.qty / asked) * 100)));
        const done = r.qty >= asked;
        // By what the rung achieves, not where it sits, so a three-change
        // solution still reads correctly. Red is the do-nothing state that
        // fails, amber is short of the order, teal is the one that arrives —
        // the same three meanings these colours carry everywhere else.
        const tone = done ? TEAL : i === 0 ? RED : AMBER;
        return (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12,
                          fontSize: 12.5, marginBottom: 3 }}>
              <span style={{ color: i === 0 ? MUTED : TEXT }}>
                {i > 0 && <span style={{ color: tone, fontWeight: 700 }}>+ </span>}
                {r.label}
              </span>
              <span style={{ color: tone, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                             whiteSpace: 'nowrap' }}>
                {nf(r.qty)}{done && ' ✓'}
              </span>
            </div>
            <div style={{ height: 7, background: '#11151a', borderRadius: 4,
                          border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: tone,
                            borderRadius: 3, transition: 'width .3s' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── declare the window the answer needs (Shift Windows phase 3) ─────────────
//
// The extra shifts above are worked out for this order alone. Declaring them
// as a window makes them real for the machine: the board, staffing and the
// re-flow all know. Said plainly before it is pressed, because a window is a
// spending decision and applies to everything on that machine those days.
function DeclareWindow({ d, result }) {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [preset, setPreset] = useState('split');
  const [state, setState] = useState(null);   // null | 'saving' | 'done' | error text
  useEffect(() => {
    if (!open || info) return;
    api.get('/api/v1/staffing/window-people?shifts=2').then(r => setInfo(r.data)).catch(() => setInfo({}));
  }, [open, info]);
  const dates = [...(d.dates || [])].sort();
  const from = dates[0], to = dates[dates.length - 1];
  const where = (d.machines || []).length ? d.machines.join(', ') : 'the machine';
  const ppl = p => (p ? (Number(p.people_min) === Number(p.people_max) ? `${p.people_min}` : `${p.people_min}–${p.people_max}`) : '—');
  const line = k => {
    const pr = info && info.presets && info.presets[k];
    return pr ? `morning ${ppl(pr[1])}, afternoon ${ppl(pr[2])}` : '';
  };
  async function declare() {
    setState('saving');
    try {
      await api.post('/api/v1/shift-windows', {
        machine_ids: d.machineIds, start_date: from, end_date: to, shifts: 2,
        people: info && info.presets ? info.presets[preset] : null,
        reason: `Can we take this order? ${result && result.requested_qty ? `${nf(result.requested_qty)} parts` : ''}`.trim(),
      });
      setState('done');
    } catch (e) {
      setState(e.response?.data?.error || 'Could not declare the window');
    }
  }
  if (state === 'done') {
    return (
      <div style={{ marginTop: 9, padding: '10px 12px', background: `${TEAL}12`, border: `1px solid ${TEAL}44`, borderRadius: 8, fontSize: 12.5, color: DIM, lineHeight: 1.55 }}>
        <div style={{ color: TEAL, fontWeight: 700 }}><i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />Window declared: 2 shifts on {where}, {fmtShort(from)} – {fmtShort(to)}.</div>
        Anything planned on {where} those days now gets the afternoon. Work already on the board keeps its dates until you re-flow it:
        Schedule → ☰ → Shift Windows → Preview re-flow. Then schedule the order.
      </div>
    );
  }
  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        style={{ marginTop: 9, background: 'none', border: `1px solid ${TEAL}66`, color: TEAL, borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        <i className="fa-solid fa-layer-group" style={{ marginRight: 6 }} />Declare this window
      </button>
    );
  }
  const chip = on => ({ padding: '6px 10px', fontSize: 12, borderRadius: 7, cursor: 'pointer', textAlign: 'left',
    border: `1px solid ${on ? TEAL : BORDER}`, background: on ? `${TEAL}14` : 'transparent', color: on ? TEAL : DIM });
  return (
    <div style={{ marginTop: 9, padding: '11px 13px', background: SUNK, border: `1px solid ${BORDER}`, borderRadius: 9, fontSize: 12.5, color: DIM, lineHeight: 1.55 }}>
      <div style={{ color: TEXT, fontWeight: 600 }}>2 shifts on {where}, {fmtShort(from)} – {fmtShort(to)}</div>
      {d.first && <div style={{ color: AMBER, marginTop: 3 }}>{d.first}</div>}
      <div style={{ marginTop: 3 }}>It applies to everything planned on {where} those days, not only this order, and work already on the board keeps its dates until you re-flow it.</div>
      {info && info.presets ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          <button type="button" style={chip(preset === 'split')} onClick={() => setPreset('split')}>Split my people · {line('split')}</button>
          <button type="button" style={chip(preset === 'extra')} onClick={() => setPreset('extra')}>Extra people · {line('extra')}</button>
        </div>
      ) : <div style={{ color: MUTED, marginTop: 6 }}>{info ? 'Your usual people are not set yet (Admin → Staffing).' : 'Reading your usual people…'}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: `1px solid ${BORDER}`, color: DIM, borderRadius: 7, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
        <button onClick={declare} disabled={state === 'saving'}
          style={{ background: TEAL, border: 'none', color: '#04201d', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          {state === 'saving' ? 'Declaring…' : 'Declare window'}
        </button>
      </div>
      {state && state !== 'saving' && <div style={{ color: '#fca5a5', marginTop: 6 }}>{state}</div>}
    </div>
  );
}
